import { computed, inject, Injectable, signal } from '@angular/core';
import { STANDARD_FOUR_TIME_ZONE_ID, UtilityService } from '@eforall/common';
import { DialogService } from '@eforall/dashboard';
import { AccTeamDialogData, AccTeamUpdateValues, DropoutUpsertValues } from '@interfaces';
import { environment } from '../../../../environments/environment';
import { AppService, FuncService } from "../../../../services";


@Injectable({
	providedIn: 'root'
})
export class AccTeamDialogDataService {

	private util = inject(UtilityService);
	private func = inject(FuncService);
	private app = inject(AppService);
	private dialogService = inject(DialogService);
	public dialogData = signal<AccTeamDialogData | undefined>(undefined);

	public canEdit = computed(() => this.app.data().permissions.canFacilitateCohorts);

	public canEditMentors = computed(() => this.app.data().permissions.canCoordinateVolunteers);

	public async loadData(accTeamId: number) {
		const data = await this.func.accTeam.getDialogData({ accTeamId });
		if (!data) {
			this.dialogData.set(data);
			return;
		}

		this.dialogData.set({
			...data,
			meetingInfo: data.meetingStartSlot != undefined && data.meetingStartSlot !== null ? this.util.date.formatAccMeetingSlot(data.meetingStartSlot, STANDARD_FOUR_TIME_ZONE_ID.Eastern, true, this.app.data().standardLocalTimezoneId) : undefined
		});
	}

	// use this method to reload the dialog data
	public async reloadData() {
		const accTeamId = this.dialogData()?.accTeamId;
		if (!accTeamId) return;

		await this.loadData(accTeamId);
	}

	public async updateTeamDetails(updatedValues: AccTeamUpdateValues) {
		const accTeamId = this.dialogData()?.accTeamId;
		if (!accTeamId) return;

		await this.func.accTeam.update({ accTeamId, values: updatedValues });
		await this.loadData(accTeamId);
	}

	public async addMentorDropout(accMentorId: number, values: DropoutUpsertValues) {
		await this.func.accTeam.addMentorDropOut({ accMentorId, values });
		await this.reloadData();
	}

	public async updateMentorDropout(accMentorId: number, dropoutId: number, values: DropoutUpsertValues) {
		await this.func.accTeam.updateMentorDropOut({ accMentorId, dropoutId, values });
		await this.reloadData();
	}

	public async scheduleTeamMeeting() {

		// Restrict creating Google Calendar events in non-PROD environments unless the user has softwareDev permission.
		if (environment.name !== 'PROD') {
			let proceed = false;
			if (this.app.data().permissions.isSoftwareDev) {
				proceed = await this.dialogService.confirm("WARNING - THIS CANNOT BE UNDONE: This will create live Google Calendar or Google Meet events with the current data in DEV. This should not be run unless the program has been configured with fake data or staff members for testing purposes. PROCEED WITH CAUTION. Do you want to continue?");
			} else {
				await this.dialogService.showMessage('Restricted in Non-PROD environments', 'You do not have permission to create Google Meet or Google Calendar events in DEV. Please contact tech@eforall.org for more details.', 'Okay', [], 300, 300);
			}
			if (!proceed) return;
		}

		const accTeamId = this.dialogData()?.accTeamId;
		if (!accTeamId) return;

		await this.func.googleCalendar.accTeam.createTeamMeetCalendarEvent({ accTeamId });
		await this.reloadData();
	}

	public async updateTeamMeetingInvitees() {
		// Restrict refreshing Google Calendar events in non-PROD environments unless the user has softwareDev permission.
		if (environment.name !== 'PROD') {
			let proceed = false;
			if (this.app.data().permissions.isSoftwareDev) {
				proceed = await this.dialogService.confirm("WARNING - THIS CANNOT BE UNDONE: This will refresh live Google Calendar or Google Meet event invitees with the current data in DEV. This should not be run unless the program has been configured with fake data or staff members for testing purposes. PROCEED WITH CAUTION. Do you want to continue?");
			} else {
				await this.dialogService.showMessage('Restricted in Non-PROD environments', 'You do not have permission to refresh Google Meet or Google Calendar event invitees in DEV. Please contact tech@eforall.org for more details.', 'Okay', [], 300, 300);
			}
			if (!proceed) return;
		}

		const accTeamId = this.dialogData()?.accTeamId;
		if (!accTeamId) return;

		await this.func.googleCalendar.accTeam.refreshAccTeamCalendarEventAttendees({ accTeamId });
		await this.reloadData();
	}
}